import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ClipboardCheck, RotateCcw } from "lucide-react";

const BIZ_TYPES = ["Proprietorship", "Partnership", "LLP", "Private Limited", "Other"];
const INDUSTRIES = ["Food", "Manufacturing", "Technology", "Retail", "E-commerce", "Logistics", "Services", "Export", "Other"];
const STATES = [
  "Telangana", "Andhra Pradesh", "Karnataka", "Maharashtra", "Delhi", "Tamil Nadu",
  "Gujarat", "West Bengal", "Kerala", "Rajasthan", "Uttar Pradesh", "Other",
];
const ACTIVITIES = ["Product", "Service", "Manufacturing", "Import", "Export", "Online", "Physical Store"];

const STATUS_STYLE = {
  Required: "border-[#FF8800]/50 text-[#FF8800]",
  "Potentially Applicable": "border-[#00B0FF]/50 text-[#00B0FF]",
  "Usually Not Applicable": "border-slate-600/60 text-slate-500",
  "Needs Professional Review": "border-[#00E676]/50 text-[#00E676]",
};

function computeRoadmap({ bizType, industry, activities }) {
  const items = [
    { name: `Business Registration — ${bizType}`, status: "Required" },
    { name: "PAN & tax registrations", status: "Required" },
  ];
  const gstLikely =
    activities.some((x) => ["Product", "Export", "Online", "Import"].includes(x)) ||
    ["Food", "Manufacturing", "Retail", "E-commerce", "Export"].includes(industry);
  items.push({ name: "GST Registration", status: gstLikely ? "Required" : "Potentially Applicable" });
  items.push({ name: "MSME / Udyam Registration", status: "Potentially Applicable" });
  items.push({
    name: "Shops & Establishment / local registrations",
    status: activities.includes("Physical Store") ? "Required" : "Potentially Applicable",
  });
  items.push({
    name: "FSSAI Registration / License (food businesses)",
    status: industry === "Food" ? "Required" : "Usually Not Applicable",
  });
  const trade = activities.includes("Import") || activities.includes("Export") || industry === "Export";
  items.push({ name: "Import Export Code (IEC)", status: trade ? "Required" : "Usually Not Applicable" });
  items.push({ name: "Trademark / IP protection", status: "Potentially Applicable" });
  const regulated = ["Manufacturing", "Food"].includes(industry);
  items.push({
    name: "Industry-specific licenses",
    status: regulated ? "Needs Professional Review" : "Potentially Applicable",
  });
  items.push({ name: "Professional Tax (state-dependent)", status: "Needs Professional Review" });
  items.push({ name: "ISO / quality certifications", status: "Potentially Applicable" });
  return items;
}

export function SmartCheck() {
  const [bizType, setBizType] = useState("");
  const [industry, setIndustry] = useState("");
  const [state, setStateVal] = useState("");
  const [city, setCity] = useState("");
  const [activities, setActivities] = useState([]);
  const [result, setResult] = useState(null);

  const toggleActivity = (a) =>
    setActivities((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]));

  const onSubmit = (e) => {
    e.preventDefault();
    setResult(computeRoadmap({ bizType, industry, activities }));
  };

  const reset = () => {
    setResult(null);
    setBizType("");
    setIndustry("");
    setStateVal("");
    setCity("");
    setActivities([]);
  };

  return (
    <section id="smart-check" data-testid="smart-check-section" className="relative py-20 sm:py-28 lg:py-32">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,176,255,0.05),transparent_55%)] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-14"
        >
          <p className="font-mono-tech text-xs tracking-[0.35em] text-[#00B0FF] uppercase mb-4">Smart Registration Check</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white" data-testid="smart-check-headline">
            WHAT DOES YOUR <span className="text-gradient-saffron">BUSINESS NEED?</span>
          </h2>
          <p className="mt-5 text-base text-slate-400 leading-relaxed">
            Answer a few questions and get an indicative requirement roadmap for your business.
          </p>
        </motion.div>

        <div className="max-w-3xl mx-auto glass rounded-2xl p-6 sm:p-10">
          <AnimatePresence mode="wait">
            {!result ? (
              <motion.form
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onSubmit={onSubmit}
                className="space-y-6"
                data-testid="smart-check-form"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Business Type *</Label>
                    <Select value={bizType} onValueChange={setBizType} required>
                      <SelectTrigger data-testid="smart-check-type" className="bg-[#06080E] border-white/10 text-slate-200">
                        <SelectValue placeholder="Select business type" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0D111D] border-white/10 text-slate-200">
                        {BIZ_TYPES.map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Industry *</Label>
                    <Select value={industry} onValueChange={setIndustry} required>
                      <SelectTrigger data-testid="smart-check-industry" className="bg-[#06080E] border-white/10 text-slate-200">
                        <SelectValue placeholder="Select industry" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0D111D] border-white/10 text-slate-200">
                        {INDUSTRIES.map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">State *</Label>
                    <Select value={state} onValueChange={setStateVal} required>
                      <SelectTrigger data-testid="smart-check-state" className="bg-[#06080E] border-white/10 text-slate-200">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0D111D] border-white/10 text-slate-200">
                        {STATES.map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">City</Label>
                    <Input
                      data-testid="smart-check-city"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="Your city"
                      className="bg-[#06080E] border-white/10 text-slate-200"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="text-slate-300">Business Activity * (select all that apply)</Label>
                  <div className="flex flex-wrap gap-2.5" data-testid="smart-check-activities">
                    {ACTIVITIES.map((a) => (
                      <button
                        type="button"
                        key={a}
                        data-testid={`smart-check-activity-${a.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => toggleActivity(a)}
                        aria-pressed={activities.includes(a)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                          activities.includes(a)
                            ? "bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E]"
                            : "glass text-slate-300 hover:text-white"
                        }`}
                      >
                        {a}
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid="smart-check-submit"
                  disabled={!bizType || !industry || !state || activities.length === 0}
                  className="w-full bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold h-12"
                >
                  <ClipboardCheck className="w-4 h-4 mr-2" /> GENERATE MY REQUIREMENT ROADMAP
                </Button>
              </motion.form>
            ) : (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="smart-check-result"
              >
                <p className="font-mono-tech text-[10px] tracking-[0.35em] text-[#00B0FF] uppercase mb-2">
                  {bizType} · {industry} · {state}{city ? ` · ${city}` : ""}
                </p>
                <h3 className="font-display text-2xl font-bold text-white mb-6">YOUR REQUIREMENT ROADMAP</h3>
                <div className="space-y-2.5">
                  {result.map((item) => (
                    <div
                      key={item.name}
                      data-testid={`smart-check-item-${item.name.slice(0, 20).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 glass rounded-xl px-5 py-3.5"
                    >
                      <span className="text-sm text-slate-200">{item.name}</span>
                      <span
                        className={`shrink-0 font-mono-tech text-[10px] tracking-[0.14em] uppercase px-3 py-1 rounded-full border ${STATUS_STYLE[item.status]}`}
                      >
                        {item.status}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="mt-6 text-xs text-slate-500 leading-relaxed" data-testid="smart-check-disclaimer">
                  This is an indicative, automated estimate for guidance only — not legal advice.
                  Actual requirements depend on your exact business activity, location, entity
                  type and applicable laws, and should be confirmed with the relevant authority
                  or a qualified professional.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Button onClick={reset} variant="outline" data-testid="smart-check-reset" className="border-white/15 text-slate-300 hover:bg-white/5">
                    <RotateCcw className="w-4 h-4 mr-2" /> Check Another Business
                  </Button>
                  <a href="#reg-closing" data-testid="smart-check-cta">
                    <Button className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold">
                      DISCUSS WITH LAUNCHLAB
                    </Button>
                  </a>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
